import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { todayLocalISO } from "@/lib/date-utils";

export type ModuloRegraCobranca = "gratuito" | "mensal" | "manual" | "plano" | "teste";
export type ModuloEscopo = "cliente" | "clinica";
export type ModuloStatus = "Ativo" | "Inativo";
export type ModuloClienteStatus = "Ativo" | "Solicitado" | "Bloqueado" | "Cancelado" | "Teste";

export type ModuloSaas = {
  id: string;
  chave: string;
  nome: string;
  descricao?: string;
  categoria?: string;
  status: ModuloStatus;
  escopo: ModuloEscopo;
  regra_cobranca: ModuloRegraCobranca;
  valor_mensal?: number;
  bloqueio_ativo?: boolean;
  ordem?: number;
};

export type ModuloCliente = {
  id: string;
  modulo_id: string;
  modulo_chave: string;
  cliente_id: string;
  clinica_id?: string;
  status: ModuloClienteStatus;
  escopo_aplicado: ModuloEscopo;
  valor_mensal?: number;
  inicio?: string;
  fim?: string;
  observacoes?: string;
};

export const defaultModulosSaas: Array<Omit<ModuloSaas, "id">> = [
  {
    chave: "atendimento_ia_whatsapp",
    nome: "Atendimento IA e WhatsApp",
    descricao: "Assistente com ChatGPT, WhatsApp, base de conhecimento, humano e lembretes por email/WhatsApp.",
    categoria: "Automação",
    status: "Ativo",
    escopo: "cliente",
    regra_cobranca: "mensal",
    valor_mensal: 149.9,
    bloqueio_ativo: false,
    ordem: 5,
  },
  {
    chave: "crm",
    nome: "CRM e leads",
    descricao: "Captação, funil comercial e contatos de pacientes interessados.",
    categoria: "Comercial",
    status: "Ativo",
    escopo: "cliente",
    regra_cobranca: "mensal",
    valor_mensal: 49.9,
    bloqueio_ativo: false,
    ordem: 10,
  },
  {
    chave: "portal_paciente",
    nome: "Portal do paciente",
    descricao: "Link público com consultas, documentos e informações liberadas ao paciente.",
    categoria: "Paciente",
    status: "Ativo",
    escopo: "cliente",
    regra_cobranca: "mensal",
    valor_mensal: 39.9,
    bloqueio_ativo: false,
    ordem: 20,
  },
  {
    chave: "documentos_atestados",
    nome: "Documentos clínicos",
    descricao: "Atestados, receituários, recibos, contratos e modelos impressos da clínica.",
    categoria: "Clínico",
    status: "Ativo",
    escopo: "cliente",
    regra_cobranca: "gratuito",
    valor_mensal: 0,
    bloqueio_ativo: false,
    ordem: 30,
  },
  {
    chave: "pagamentos_funcionarios",
    nome: "Pagamento de funcionários",
    descricao: "Fechamento individual, metas, comissões e recibos de pagamento interno.",
    categoria: "Financeiro",
    status: "Ativo",
    escopo: "cliente",
    regra_cobranca: "mensal",
    valor_mensal: 59.9,
    bloqueio_ativo: false,
    ordem: 40,
  },
  {
    chave: "controle_pagamentos",
    nome: "Controle de pagamentos",
    descricao: "Pix, transferências, cartão, cobranças e conciliação financeira.",
    categoria: "Financeiro",
    status: "Ativo",
    escopo: "cliente",
    regra_cobranca: "mensal",
    valor_mensal: 49.9,
    bloqueio_ativo: false,
    ordem: 50,
  },
  {
    chave: "relatorios_avancados",
    nome: "Relatórios avançados",
    descricao: "Indicadores financeiros, metas, comissão e visão consolidada por unidade.",
    categoria: "Gestão",
    status: "Ativo",
    escopo: "cliente",
    regra_cobranca: "mensal",
    valor_mensal: 69.9,
    bloqueio_ativo: false,
    ordem: 60,
  },
  {
    chave: "estoque_laboratorio",
    nome: "Estoque e laboratório",
    descricao: "Materiais, fornecedores, laboratório, patrimônio e controle operacional.",
    categoria: "Operação",
    status: "Ativo",
    escopo: "cliente",
    regra_cobranca: "mensal",
    valor_mensal: 49.9,
    bloqueio_ativo: false,
    ordem: 70,
  },
];

const isSuperAdminUser = (user?: { role?: string; email?: string } | null) =>
  user?.role === "superadmin" || user?.email === "suporte@cloudservidores.com";

const activeClinicaId = () => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("odonto.activeClinicaId") || "";
};

export function useModulosSaas() {
  const { user } = useAuth();
  const [modulos, setModulos] = useState<ModuloSaas[]>([]);
  const [liberacoes, setLiberacoes] = useState<ModuloCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeClinic, setActiveClinic] = useState(activeClinicaId());

  const clienteId = user?.cliente_id || "default";
  const isSuperAdmin = isSuperAdminUser(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogo, vinculados] = await Promise.all([
        apiList<ModuloSaas>("modulos_saas"),
        apiList<ModuloCliente>("modulos_cliente"),
      ]);
      setModulos(catalogo.sort((a, b) => Number(a.ordem || 999) - Number(b.ordem || 999)));
      setLiberacoes(vinculados);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const sync = () => setActiveClinic(activeClinicaId());
    window.addEventListener("odonto-clinic-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("odonto-clinic-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const modulosAtivos = useMemo(() => modulos.filter((modulo) => modulo.status !== "Inativo"), [modulos]);

  const getModulo = useCallback(
    (chave: string) => modulosAtivos.find((modulo) => modulo.chave === chave),
    [modulosAtivos]
  );

  const liberacoesDoCliente = useCallback(
    (targetClienteId = clienteId) => liberacoes.filter((item) => item.cliente_id === targetClienteId),
    [clienteId, liberacoes]
  );

  const getLiberacao = useCallback(
    (chave: string, targetClienteId = clienteId, clinicaId = activeClinic) =>
      liberacoes.find((item) => {
        if (item.cliente_id !== targetClienteId || item.modulo_chave !== chave) return false;
        if (item.escopo_aplicado === "clinica" && item.clinica_id) return item.clinica_id === clinicaId;
        return true;
      }),
    [activeClinic, clienteId, liberacoes]
  );

  const hasModulo = useCallback(
    (chave: string, targetClienteId = clienteId, clinicaId = activeClinic) => {
      if (isSuperAdmin) return true;
      const modulo = getModulo(chave);
      if (!modulo) return true;
      if (modulo.regra_cobranca === "gratuito" || modulo.regra_cobranca === "plano") return true;
      if (!modulo.bloqueio_ativo) return true;
      const liberacao = getLiberacao(chave, targetClienteId, clinicaId);
      return liberacao?.status === "Ativo" || liberacao?.status === "Teste";
    },
    [activeClinic, clienteId, getLiberacao, getModulo, isSuperAdmin]
  );

  const upsertModulo = async (input: Partial<ModuloSaas> & { chave: string; nome: string }) => {
    const current = modulos.find((modulo) => modulo.id === input.id || modulo.chave === input.chave);
    const payload = {
      status: "Ativo",
      escopo: "cliente",
      regra_cobranca: "manual",
      valor_mensal: 0,
      bloqueio_ativo: false,
      ...input,
    };
    if (current) await apiUpdate<ModuloSaas>("modulos_saas", current.id, payload);
    else await apiCreate<ModuloSaas>("modulos_saas", payload);
    await load();
  };

  const upsertLiberacao = async (input: Partial<ModuloCliente> & { modulo_chave: string; cliente_id: string }) => {
    const modulo = getModulo(input.modulo_chave) || modulos.find((item) => item.chave === input.modulo_chave);
    const current = liberacoes.find((item) => {
      const sameScope = input.escopo_aplicado === "clinica"
        ? item.clinica_id === input.clinica_id
        : item.escopo_aplicado !== "clinica";
      return item.cliente_id === input.cliente_id && item.modulo_chave === input.modulo_chave && sameScope;
    });
    const payload = {
      modulo_id: modulo?.id || input.modulo_id || input.modulo_chave,
      escopo_aplicado: modulo?.escopo || "cliente",
      valor_mensal: modulo?.valor_mensal || 0,
      inicio: todayLocalISO(),
      status: "Solicitado" as ModuloClienteStatus,
      ...input,
    };
    if (current) await apiUpdate<ModuloCliente>("modulos_cliente", current.id, payload);
    else await apiCreate<ModuloCliente>("modulos_cliente", payload);
    await load();
  };

  const removeModulo = async (id: string) => {
    await apiDelete("modulos_saas", id);
    await load();
  };

  const removeLiberacao = async (id: string) => {
    await apiDelete("modulos_cliente", id);
    await load();
  };

  const seedDefaultModulos = async () => {
    for (const modulo of defaultModulosSaas) {
      if (!modulos.some((item) => item.chave === modulo.chave)) {
        await apiCreate<ModuloSaas>("modulos_saas", modulo);
      }
    }
    await load();
  };

  return {
    modulos,
    modulosAtivos,
    liberacoes,
    loading,
    clienteId,
    activeClinic,
    isSuperAdmin,
    load,
    getModulo,
    getLiberacao,
    liberacoesDoCliente,
    hasModulo,
    upsertModulo,
    upsertLiberacao,
    removeModulo,
    removeLiberacao,
    seedDefaultModulos,
  };
}
